import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import queryString from 'query-string';
import { DocumentHeader } from '../../components';
import { FileUpload } from '../../container';
import Layout from '../../layout';
import { DocumentContext } from '../../context/DocumentProvider';
import { tryParseJson, priorityScrollTo, trimStr } from 'lib/utils/helper';
import { getTextToHighlight } from './highlighter';
import PdfFileViewer from '../PDFViewer/PdfFileViewer';
import BodySection from './BodySection';
import GlobalStyles from '../../styles/Global.style';
import { escapeRegExp } from 'lodash';

export const CDAMainViewer = ({
  ehrRecord,
  requestPatientRecord,
  isNewRecordAvailable,
  exportCDAAsPDF,
  exportCDAAsPDFLoading,
  setExportCDAAsPDFLoading
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cdaUrl = queryString.parse(searchParams.toString())?.cdaUrl;
  const [rowsToExport, setRowsToExport] = useState([]);
  const [fileName, setFileName] = useState('');
  const [resetRows, setResetRows] = useState(false);
  const { currentDocument, rawDocument } = useContext(DocumentContext);
  const params = queryString.parse(searchParams.toString() || '');

  const getRowsToHighlight = (searchDataGroupedByCategory, searchText) => {
    return getTextToHighlight(searchDataGroupedByCategory, searchText);
  };

  const mainHighlighter = () => {
    console.log('Running main highlighter with params.q: ', params.q);
    let filteredData = {};
    let found = false;
    let searchDataToUse = currentDocument.searchData;

    if (params.section) {
      const normalizedSection = params.section.toLowerCase().trim();
      searchDataToUse = currentDocument.searchData.filter(
        (suggestion) =>
          suggestion.groupTitle?.toLowerCase().trim() === normalizedSection
      );

      if (!searchDataToUse.length) {
        searchDataToUse = currentDocument.searchData;
      }
    }

    searchDataToUse.forEach((suggestion) => {
      filteredData[suggestion.groupTitle] = [];

      const sortData = suggestion.data
        .map((i) => {
          if (i && i.text) {
            const normalizedData = trimStr(i.plainRow || i.text);
            const normalizedInput = trimStr(params.q);
            const inputRegex = new RegExp(
              `\\b${escapeRegExp(normalizedInput)}\\b`,
              'i'
            );

            if (normalizedData === normalizedInput) {
              return {
                priority: 1,
                data: i
              };
            } else if (inputRegex.test(normalizedData)) {
              return {
                priority: 0.75,
                data: i
              };
            } else if (normalizedData.includes(normalizedInput)) {
              return {
                priority: 0.5,
                data: i
              };
            } else {
              return {
                priority: 0,
                data: i
              };
            }
          }

          return {
            priority: 0,
            data: i
          };
        })
        .reduce((acc, val) => (val.priority > acc.priority ? val : acc), {
          priority: 0,
          data: null
        });

      if (sortData.priority > 0) {
        filteredData[suggestion.groupTitle].push(sortData.data);
      }
    });

    found = Object.keys(filteredData).some((category) => {
      if (filteredData[category] && filteredData[category][0]) {
        const type = filteredData[category][0].type;
        const section = filteredData[category][0].section;
        const text = filteredData[category][0].text;
        const rowId = filteredData[category][0].rowId;

        const currentParams = {};

        for (let entry of searchParams.entries()) {
          currentParams[entry[0]] = entry[1];
        }

        const newParams = {
          ...currentParams,
          q: ''
        };

        if (type === 'table' && rowId) {
          newParams.rowId = rowId;
          newParams.search = params.q;
        }

        if (type === 'text' && rowId && text) {
          newParams.rowId = rowId;
          newParams.search = params.q;
        }

        console.log('newParams: ', newParams);

        setSearchParams(newParams);

        return true;
      } else {
        return false;
      }
    });

    return found;
  };

  useEffect(() => {
    if (params.rowId) {
      priorityScrollTo(params.rowId, 3);
    } else if (params.rowIds) {
      const rowIds = tryParseJson(params.rowIds);
      if (rowIds?.length) {
        priorityScrollTo(rowIds[0], 3);
      }
    } else if (params.section) {
      priorityScrollTo(params.section, 3, { offset: 0 });
    }

    if (params.q && currentDocument) {
      const foundHighlight = mainHighlighter();

      console.log('foundHighlight: ', foundHighlight);

      if (!foundHighlight) {
        console.log('fallback highlight');

        let searchDataGroupedByCategory = {};
        let searchDataToUse = currentDocument.searchData;
        if (params.section) {
          const normalizedSection = params.section.toLowerCase().trim();
          searchDataToUse = currentDocument.searchData.filter(
            (suggestion) =>
              suggestion.groupTitle?.toLowerCase().trim() === normalizedSection
          );

          if (!searchDataToUse.length) {
            searchDataToUse = currentDocument.searchData;
          }
        }

        searchDataToUse.forEach((item) => {
          searchDataGroupedByCategory[item.groupTitle] = item.data;
        });

        let textToHighlight = getRowsToHighlight(
          searchDataGroupedByCategory,
          params.q
        );

        if (!textToHighlight?.length) {
          searchDataGroupedByCategory = {};
          currentDocument.searchData.forEach((item) => {
            searchDataGroupedByCategory[item.groupTitle] = item.data;
          });

          textToHighlight = getRowsToHighlight(
            searchDataGroupedByCategory,
            params.q
          );
        }

        if (textToHighlight?.length) {
          console.log('textToHighlight: ', textToHighlight);

          const newSearchParams = {
            ...Object.fromEntries(searchParams),
            q: '',
            rowIds: JSON.stringify(textToHighlight),
            search: params.q
          };
          if (cdaUrl) newSearchParams.cdaUrl = cdaUrl;
          setSearchParams(newSearchParams);
        }
      }
    }
  }, [currentDocument, searchParams]);

  if (params.pdfUrl) {
    return (
      <PdfFileViewer
        pdfUrl={params.pdfUrl}
        fileName={params.fileName}
        previewPage={params.previewPage}
        boundingBox={
          params?.boundingBox ? JSON.parse(params?.boundingBox) : null
        }
        imageDimension={
          params?.imageDimension ? JSON.parse(params.imageDimension) : null
        }
        ocr={params?.ocr?.toLowerCase() === 'true'}
        searchText={params.searchText}
        metaDataPreview={JSON.parse(params.metaDataPreview)}
        metaDataDebug={JSON.parse(params.metaDataDebug)}
      />
    );
  }

  if (!currentDocument) {
    return <FileUpload ehrRecord={ehrRecord} setFileName={setFileName} />;
  }

  const { header, body } = currentDocument;

  const resetRowsSelection = () => {
    setRowsToExport([]);

    navigate(
      {
        search: ''
      },
      { replace: true }
    );
    setResetRows(true);

    setTimeout(() => {
      setResetRows(false);
    }, 500);
  };

  return (
    <>
      <GlobalStyles />
      <motion.div>
        <Layout
          currentDocument={currentDocument}
          requestPatientRecord={requestPatientRecord}
          isNewRecordAvailable={isNewRecordAvailable}
          rowsToExport={rowsToExport}
          resetRowsSelection={resetRowsSelection}
          rawDocument={rawDocument}
          ehrRecord={ehrRecord}
          fileName={fileName}
          exportCDAAsPDF={exportCDAAsPDF}
          exportCDAAsPDFLoading={exportCDAAsPDFLoading}
          setExportCDAAsPDFLoading={setExportCDAAsPDFLoading}
          documentTitle={params.title}
        >
          <DocumentHeader documentHeaderData={header} />
          {body &&
            body.map((section, i) => (
              <BodySection
                key={i}
                section={section}
                setRowsToExport={setRowsToExport}
                resetRows={resetRows}
                currentDocument={currentDocument}
                rawDocument={rawDocument}
              />
            ))}
        </Layout>
      </motion.div>
    </>
  );
};
